import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import Bugsnag from "@bugsnag/js";
import { faFileUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/pro-solid-svg-icons";
import Modal from "../../../ui-elements/Modal/Modal";
import Button from "../../../ui-elements/Button/Button";
import Checkbox from "../../../ui-elements/Input/Formik/Checkbox";
import Input from "../../../ui-elements/Input/Formik/Input";
import Link from "../../../ui-elements/Link/Link";
import Loading from "../../../ui-elements/Loading/Loading";
import Table from "../../../ui-elements/Table/Table";
import FileUpload from "../PepFileUpload";
import { CompanyWatchModalProps, AdilarRole, ALL_ADILAR_ROLES, ADILAR_ROLE_LABELS, AddCompanyWatchRequest } from "../../../types/CompanyWatchTypes";
import { addCompaniesToWatchList, fetchCompanyNameByKennitala } from "../CompanyWatchHelpers/CompanyWatchApi";
import { parseFile } from "../../../services/pepWatchUtils";
import { insertDash, isCompany } from "../../../services/utils";
import * as yup from "yup";

interface IFormValues {
  companyId: string;
  name: string;
  pep: boolean;
  sanctions: boolean;
  stjorn: boolean;
  prokuruhafi: boolean;
  framkvaemdastjorn: boolean;
  raunverulegir_eigendur: boolean;
}

interface ParsedCompany {
  companyId: string;
  name: string | null;
  isValid: boolean;
}

interface FailedCompanyRecord {
  companyId: string;
  reason?: string;
}

interface FailedCompanyResult {
  failedRecords: FailedCompanyRecord[];
  successCount: number;
  totalCount: number;
}

const validationSchema = yup.object().shape({
  companyId: yup
    .string()
    .required("Kennitala er nauðsynleg")
    .test("is-company", "Kennitala er ekki gild kennitala lögaðila", (value) => {
      if (!value) return false;
      const cleaned = value.replace(/\D/g, "");
      return isCompany(cleaned);
    }),
});

// Observe changes to companyId and look up the registered name (debounced)
const CompanyNameObserver: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<IFormValues>();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastLookedUpRef = useRef<string>("");

  useEffect(() => {
    const cleaned = values.companyId.replace(/\D/g, "");
    if (cleaned.length !== 10) {
      if (values.name) setFieldValue("name", "");
      return;
    }
    if (cleaned === lastLookedUpRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      try {
        lastLookedUpRef.current = cleaned;
        const name = await fetchCompanyNameByKennitala(cleaned);
        setFieldValue("name", name ?? "");
      } catch (e) {
        if (e instanceof Error) Bugsnag.notify(e);
      }
    }, 400);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.companyId]);

  return null;
};

const CompanyWatchAddModal: React.FC<CompanyWatchModalProps> = ({
  show,
  close,
  setToastMessage,
  setToastType,
  setToastShow,
  onSuccess,
}) => {
  // File upload state
  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [parsedCompanies, setParsedCompanies] = useState<ParsedCompany[]>([]);
  const [isResolvingNames, setIsResolvingNames] = useState(false);
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);
  const [failedResult, setFailedResult] = useState<FailedCompanyResult | null>(null);

  const fileUploadRef = useRef<{ triggerFileInput: () => void; clearFileInput: () => void }>(null);

  const handleFileUploadClick = () => {
    setIsFileUploadOpen(true);
    setTimeout(() => {
      fileUploadRef.current?.triggerFileInput();
    }, 100);
  };

  const handleFileUpload = useCallback(async (file: File) => {
    setUploadedFile(file);
    setIsParsingFile(true);
    setParsedCompanies([]);
    setFailedResult(null);

    try {
      const rawData = await parseFile(file);

      // Extract company IDs from the first column (PersonId in parsedFieldData)
      const companyIds = rawData
        .map((row) => (row.PersonId ?? "").replace(/\D/g, ""))
        .filter((id) => isCompany(id));

      // Deduplicate
      const uniqueIds = [...new Set(companyIds)];

      if (uniqueIds.length === 0) {
        setParsedCompanies([]);
        setIsParsingFile(false);
        return;
      }

      // Create initial parsed list
      const initial: ParsedCompany[] = uniqueIds.map((id) => ({
        companyId: id,
        name: null,
        isValid: true,
      }));
      setParsedCompanies(initial);
      setIsParsingFile(false);

      // Resolve names in the background
      setIsResolvingNames(true);
      const resolved = [...initial];
      for (let i = 0; i < resolved.length; i++) {
        try {
          const name = await fetchCompanyNameByKennitala(resolved[i].companyId);
          resolved[i] = { ...resolved[i], name, isValid: name !== null };
        } catch {
          resolved[i] = { ...resolved[i], name: null, isValid: false };
        }
        // Update in batches of 5 for smoother UI
        if ((i + 1) % 5 === 0 || i === resolved.length - 1) {
          setParsedCompanies([...resolved]);
        }
      }
      setIsResolvingNames(false);
    } catch (error) {
      console.error("Error parsing file:", error);
      if (error instanceof Error) Bugsnag.notify(error);
      setIsParsingFile(false);
      setIsResolvingNames(false);
    }
  }, []);

  const resetFileUpload = useCallback(() => {
    setUploadedFile(null);
    setParsedCompanies([]);
    setIsParsingFile(false);
    setIsResolvingNames(false);
    setIsSubmittingBulk(false);
    setFailedResult(null);
    fileUploadRef.current?.clearFileInput();
  }, []);

  const resetFormAndClose = useCallback(() => {
    resetFileUpload();
    setIsFileUploadOpen(false);
    close();
  }, [resetFileUpload, close]);

  // Single company submit
  const handleSubmit = useCallback(
    async (values: IFormValues) => {
      try {
        const adilarIVoktun: AdilarRole[] = ALL_ADILAR_ROLES.filter(
          (role) => values[role]
        );

        const responseBody = await addCompaniesToWatchList([
          {
            companyId: values.companyId.replace("-", ""),
            pepFrequency: values.pep ? "daily" : "never",
            sanctionsFrequency: values.sanctions ? "daily" : "never",
            adilarIVoktun,
          },
        ]);

        if (responseBody.success) {
          if (responseBody.result?.result && responseBody.result.result !== 1) {
            setToastMessage(responseBody.result.descr ?? "Villa kom upp");
            setToastType("error");
            setToastShow(true);
          } else if (responseBody.result?.failed && responseBody.result.failed.length > 0) {
            const reason = responseBody.result.failed[0]?.reason;
            setToastMessage(reason || "Ekki tókst að bæta lögaðila við");
            setToastType("alert");
            setToastShow(true);
          } else {
            onSuccess();
            setToastMessage("Lögaðila bætt við í vöktun");
            setToastType("success");
            setToastShow(true);
            resetFormAndClose();
          }
        } else {
          setToastMessage(responseBody.message || "Villa kom upp");
          setToastType("error");
          setToastShow(true);
        }
      } catch (e) {
        if (e instanceof Error) Bugsnag.notify(e);
        setToastMessage("Villa kom upp við að bæta lögaðila við");
        setToastType("error");
        setToastShow(true);
      }
    },
    [resetFormAndClose, onSuccess, setToastMessage, setToastType, setToastShow]
  );

  // Bulk submit using the form's checkbox settings
  const handleBulkSubmit = useCallback(
    async (values: IFormValues) => {
      const validCompanies = parsedCompanies.filter((c) => c.isValid);
      if (validCompanies.length === 0) return;

      setIsSubmittingBulk(true);
      setFailedResult(null);

      try {
        const adilarIVoktun: AdilarRole[] = ALL_ADILAR_ROLES.filter(
          (role) => values[role]
        );

        const requests: AddCompanyWatchRequest[] = validCompanies.map((c) => ({
          companyId: c.companyId,
          pepFrequency: values.pep ? "daily" : "never",
          sanctionsFrequency: values.sanctions ? "daily" : "never",
          adilarIVoktun,
        }));

        const responseBody = await addCompaniesToWatchList(requests);

        if (responseBody.success) {
          if (responseBody.result?.result && responseBody.result.result !== 1) {
            setToastMessage(responseBody.result.descr ?? "Villa kom upp");
            setToastType("error");
            setToastShow(true);
          } else if (responseBody.result?.failed && responseBody.result.failed.length > 0) {
            const failedRecords: FailedCompanyRecord[] = responseBody.result.failed.map((f: any) => ({
              companyId: f.companyId ?? f,
              reason: f.reason,
            }));
            const successCount = requests.length - failedRecords.length;
            setFailedResult({ failedRecords, successCount, totalCount: requests.length });

            if (successCount > 0) {
              onSuccess();
              setToastMessage(`${successCount} lögaðilum bætt við, en ${failedRecords.length} tókst ekki`);
              setToastType("alert");
            } else {
              setToastMessage(`${failedRecords.length} lögaðilum tókst ekki að bæta við`);
              setToastType("error");
            }
            setToastShow(true);
          } else {
            onSuccess();
            setToastMessage(`${requests.length} lögaðilum bætt við í vöktun`);
            setToastType("success");
            setToastShow(true);
            resetFormAndClose();
          }
        } else {
          setToastMessage(responseBody.message || "Villa kom upp");
          setToastType("error");
          setToastShow(true);
        }
      } catch (e) {
        if (e instanceof Error) Bugsnag.notify(e);
        setToastMessage("Villa kom upp við að bæta lögaðilum við");
        setToastType("error");
        setToastShow(true);
      } finally {
        setIsSubmittingBulk(false);
      }
    },
    [parsedCompanies, resetFormAndClose, onSuccess, setToastMessage, setToastType, setToastShow]
  );

  const validCount = parsedCompanies.filter((c) => c.isValid).length;
  const invalidCount = parsedCompanies.filter((c) => !c.isValid).length;

  const modalBody = (
    <div>
      <Formik
        initialValues={{
          companyId: "",
          name: "",
          pep: true,
          sanctions: false,
          stjorn: false,
          prokuruhafi: true,
          framkvaemdastjorn: false,
          raunverulegir_eigendur: true,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form>
            <h3>{!failedResult ? "Bæta við lögaðilum" : "Niðurstöður sendingar"}</h3>

            {/* Failed results view */}
            {failedResult && (
              <>
                <span className="paragraph-xsmall paragraph--italic bulk-results__hint">
                  Gögn gætu þegar verið í vöktun eða eitthvað fór úrskeiðis við sendingu.
                </span>
                <div className="bulk-results__table-wrapper">
                  <Table
                    aSide={
                      <div className="bulk-results__stats">
                        <div>
                          <FontAwesomeIcon icon={faCheck} className="bulk-results__icon--success" />{` Tókst: ${failedResult.successCount}`}
                        </div>
                        <div>
                          <FontAwesomeIcon icon={faTimes} className="bulk-results__icon--error" />{` Mistókst: ${failedResult.failedRecords.length}`}
                        </div>
                      </div>
                    }
                    withPagination="auto"
                    paginationConfig={{ usePortal: true, itemsPerPage: 10 }}
                    data={failedResult.failedRecords.map((rec) => ({
                      ...rec,
                      name: parsedCompanies.find((c) => c.companyId === rec.companyId)?.name ?? null,
                    }))}
                    columns={[
                      {
                        title: "Kennitala",
                        renderCell: ({ companyId }: any) => insertDash(companyId),
                        textAlign: "left",
                      },
                      {
                        title: "Nafn",
                        renderCell: ({ name }: any) => name || "-",
                        textAlign: "left",
                      },
                      {
                        title: "Ástæða",
                        renderCell: ({ reason }: any) => reason || "",
                        textAlign: "left",
                      },
                    ]}
                  />
                </div>
                <Button type="button" buttonType="primary" size="lg" onClick={resetFormAndClose}>
                  Loka
                </Button>
              </>
            )}

            {/* File upload section - hidden when showing results */}
            {!failedResult && (
              <div className="KCL_companyWatchAddModal">
                {/* Excel upload section */}
                <div className="fileUpload__body">
                  <span className="paragraph bulk-upload__intro-text">
                    Hægt er að bæta við lögaðilum úr Excel skjali, sjá
                  </span>
                  <Link
                    url="https://cdn.livemarketdata.com/keldan/AML_voktun_logadilar_snidmat.xlsx"
                    targetBlank
                    icon="excel"
                    className="bulk-upload__excel-link"
                    linkSize="18"
                  >
                    Sniðmát í Excel
                  </Link>
                  <Button
                    type="button"
                    icon={faFileUpload}
                    buttonType="secondary"
                    onClick={handleFileUploadClick}
                    disabled={isSubmittingBulk || isParsingFile}
                  >
                    Hlaða upp skjali
                  </Button>
                  <p className="paragraph--bold bulk-upload__pricing">
                    Verð eru samkvæmt{" "}
                    <Link linkSize="18" url="/Verdskra#Gagnaleit">
                      verðskrá Keldunnar
                    </Link>{" "}
                    eða samningi hverju sinni.
                  </p>
                  <FileUpload
                    ref={fileUploadRef}
                    isOpen={isFileUploadOpen}
                    onClose={() => setIsFileUploadOpen(false)}
                    onFileUpload={handleFileUpload}
                  />

                  {/* Parsing/loading state */}
                  {isParsingFile && (
                    <div className="bulk-upload__section">
                      <div className="bulk-upload__info-text">Að lesa skjal...</div>
                      <Loading />
                    </div>
                  )}

                  {/* Parsed companies table */}
                  {uploadedFile && !isParsingFile && parsedCompanies.length > 0 && (
                    <div className="bulk-upload__section">
                      {isResolvingNames && (
                        <div className="bulk-upload__info-text">
                          Að fletta upp nöfnum... ({parsedCompanies.filter((c) => c.name !== null).length}/{parsedCompanies.length})
                        </div>
                      )}
                      <Table
                        tableSize="lg"
                        withPagination="auto"
                        paginationConfig={{ usePortal: true, itemsPerPage: 10 }}
                        data={parsedCompanies}
                        columns={[
                          {
                            title: "Kennitala",
                            renderCell: ({ companyId }) => insertDash(companyId),
                            textAlign: "left",
                          },
                          {
                            title: "Nafn",
                            renderCell: ({ name, isValid }) =>
                              name ? name : isValid ? <Loading width="100px" count={1} /> : <span className="bulk-upload__error-text">Fannst ekki</span>,
                            textAlign: "left",
                          },
                        ]}
                      />
                      <div className="bulk-upload__counts">
                        {validCount > 0 && <span className="bulk-upload__counts--success">{validCount} gildar</span>}
                        {validCount > 0 && invalidCount > 0 && " · "}
                        {invalidCount > 0 && <span className="bulk-upload__counts--error">{invalidCount} ógildar</span>}
                      </div>
                      <div className="bulk-upload__actions">
                        <Button
                          type="button"
                          buttonType="secondary"
                          size="lg"
                          onClick={resetFileUpload}
                          disabled={isSubmittingBulk}
                        >
                          Eyða skjali
                        </Button>
                        <Button
                          type="button"
                          buttonType="primary"
                          size="lg"
                          onClick={() => handleBulkSubmit(values)}
                          disabled={validCount === 0 || isResolvingNames || isSubmittingBulk || invalidCount > 0}
                        >
                          {isSubmittingBulk
                            ? `Sendi ${validCount} lögaðila...`
                            : `Senda ${validCount} lögaðila`}
                        </Button>
                      </div>
                      {invalidCount > 0 && !isResolvingNames && (
                        <div className="bulk-upload__counts">
                          Lagaðu villurnar til að geta sent gögnin
                        </div>
                      )}
                    </div>
                  )}

                  {/* No valid data after parse */}
                  {uploadedFile && !isParsingFile && parsedCompanies.length === 0 && (
                    <div className="bulk-upload__empty">
                      Engar gildar kennitalur fundust í skjalinu.
                    </div>
                  )}
                </div>

                {/* Single company kennitala/name inputs - only when no file uploaded */}
                {!uploadedFile && (
                  <>
                    <h6>Upplýsingar um lögaðila</h6>
                    <div className="form-sections-row">
                      <div className="form-section">
                        <CompanyNameObserver />
                        <Input
                          label="Kennitala lögaðila"
                          name="companyId"
                          id="companyId"
                          inputType="ssnFormat"
                          value={values.companyId}
                        />
                      </div>
                      <div className="form-section">
                        <Input
                          label="Nafn lögaðila"
                          name="name"
                          id="name"
                          disabled
                          value={values.name}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Checkboxes - always visible */}
                <div className="form-sections-row">
                  <div className="form-section">
                    <p className="paragraph--bold">Vöktun</p>
                    <div className="checkbox-wrapper">
                      <Checkbox
                        label="Vöktun á PEP lista"
                        name="pep"
                        id="pep"
                        value={values.pep.toString()}
                      />
                      <Checkbox
                        label="Vöktun á alþjóðlegum válistum"
                        name="sanctions"
                        id="sanctions"
                        value={values.sanctions.toString()}
                      />
                    </div>
                  </div>
                  <div className="form-section">
                    <p className="paragraph--bold">Aðilar í vöktun</p>
                    <div className="checkbox-wrapper">
                      {ALL_ADILAR_ROLES.map((role) => (
                        <Checkbox
                          key={role}
                          label={ADILAR_ROLE_LABELS[role]}
                          name={role}
                          id={role}
                          value={(values[role] as boolean).toString()}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action buttons for single mode */}
                {!uploadedFile && (
                  <div className="form__actions">
                    <Button type="button" buttonType="secondary" size="lg" onClick={resetFormAndClose}>
                      Hætta við
                    </Button>
                    <Button type="submit" buttonType="primary" size="lg" disabled={!values.companyId}>
                      Staðfesta
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );

  return <Modal className="pep-watch-modal" show={show} onHide={resetFormAndClose} hasCloseButton body={modalBody} />;
};

export default CompanyWatchAddModal;
